import { getVideoEmbedURL } from '../util/video-url';

class BannerVideo {
	constructor(el) {
		this.el = el;
		this.iframe = this.el.querySelector('iframe');
		this.video = this.el.querySelector('video');
		this.videoUrl = this.el.getAttribute('data-video-url');
		this.videoType = this.el.getAttribute('data-video-type');
		this.bannerParentEl = this.el.closest('.banner');
		this.currentSize = '';

		const isHtml5Video =
			this.videoType === 'local' || this.videoType === 'external' || this.videoType === 'url';

		// Check if this is an iframe-based video (YouTube/Vimeo) or HTML5 video
		if (this.iframe && this.videoType === 'embed') {
			// Handle iframe-based video (YouTube/Vimeo)
			this.setupIframe();
		} else if (this.video && isHtml5Video) {
			// Handle HTML5 video element (local or external)
			this.setupHTML5Video();
		} else if (this.el.tagName.toLowerCase() === 'video') {
			// Handle legacy HTML5 video element (responsive videos)
			this.setupHTML5Video();
		} else {
			console.warn('BannerVideo: Invalid element. Please provide a video element or iframe with proper data attributes.');
		}
	}

	setupIframe() {
		const {iframe, videoUrl} = this;
		const embedUrl = getVideoEmbedURL(videoUrl, { background: true });
		const isYouTube =
			embedUrl.indexOf('youtube.com') !== -1 || embedUrl.indexOf('youtube-nocookie.com') !== -1;
		const isVimeo = embedUrl.indexOf('vimeo.com') !== -1;

		if (!embedUrl || (!isYouTube && !isVimeo)) {
			console.warn('BannerVideo: The video URL is not a recognized YouTube or Vimeo URL.');
			return;
		}

		iframe.setAttribute('src', embedUrl);
		iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media');
	}

	setupHTML5Video() {
		// If this is a local or external video, it's already set up in the HTML
		if (this.videoType === 'local' || this.videoType === 'external' || this.videoType === 'url') {
			// Video is already configured in the HTML, just ensure it plays
			this.video.play().catch(error => {
				console.warn('BannerVideo: Autoplay failed:', error);
				this.setVideoAutoplaySupport(false, false);
			});
			
			// Ensure loop attribute is set for HTML5 videos
			if (!this.video.hasAttribute('loop')) {
				this.video.setAttribute('loop', 'true');
			}
			
			// Add ended event listener as fallback to ensure looping
			this.video.addEventListener('ended', () => {
				this.video.currentTime = 0;
				this.video.play().catch(error => {
					console.warn('BannerVideo: Loop replay failed:', error);
				});
			});
		} else {
			// Legacy responsive video setup
			this.chooseVideoAndPoster();
		}

		// catch playback errors
		const videoElement = this.video || this.el;
		videoElement.addEventListener('error', this.catchPlaybackError);
		videoElement.addEventListener('abort', this.catchPlaybackError);

		// load a new video on resize if needed (only for legacy responsive videos)
		if (!this.videoType || this.videoType === 'responsive') {
			window.addEventListener('resize', () => this.chooseVideoAndPoster());
		}
	}

	setVideoAutoplaySupport(isSupported, removeEl = false) {
		const htmlEl = document.documentElement;

		if (isSupported) {
			htmlEl.classList.add('video-autoplay-support');
			htmlEl.classList.remove('no-video-autoplay-support');
		} else {
			htmlEl.classList.remove('video-autoplay-support');
			htmlEl.classList.add('no-video-autoplay-support');
		}

		if (removeEl) {
			this.el.remove();
		}
	}

	catchPlaybackError = () => {
		// NOTE: Safari will report 'stalled' even when the video is not stalled
		// do not listen for this event
		this.setVideoAutoplaySupport(false, false);
	};

	chooseVideoAndPoster = () => {
		const bannerWidth = window.innerWidth;
		let newSize = '';

		// figure out which device size we're looking at
		if (bannerWidth >= 1024) {
			newSize = 'desktop';
		} else if (bannerWidth >= 641) {
			newSize = 'tablet';
		} else {
			newSize = 'mobile';
		}

		// no need to make changes if the device size hasn't changed
		if (this.currentSize === newSize) {
			return;
		}

		this.currentSize = newSize;

		// select the video and poster URLs to use
		let videoUrl = '';
		let posterUrl = '';

		switch (this.currentSize) {
			case 'desktop':
				videoUrl = this.el.getAttribute('data-video-desktop');
				posterUrl = this.el.getAttribute('data-poster-desktop');
				break;

			case 'tablet':
				videoUrl = this.el.getAttribute('data-video-tablet');
				posterUrl = this.el.getAttribute('data-poster-tablet');
				break;

			case 'mobile':
				videoUrl = this.el.getAttribute('data-video-mobile');
				posterUrl = this.el.getAttribute('data-poster-mobile');
				break;

			default:
				break;
		}

		// ensure we have a video URL
		if (videoUrl) {
			// pause the current video, set/load the new URL, and begin playback
			this.el.pause();

			this.el.setAttribute('src', videoUrl);
			this.el.setAttribute('poster', posterUrl);

			this.el.load();
			this.el.play();
		} else {
			// fallback to banner image for this size if no video URL is set
			this.setVideoAutoplaySupport(false);
		}
	};
}

export default BannerVideo;
